import React, { useMemo } from 'react';
import { CallErrorCode } from 'matrix-js-sdk/lib/webrtc/call';
import { getCallMediaErrorPresentation } from '../../../../../shared/services/call/callMediaErrors';

const getCallErrorPresentation = (error) => {
  if (!error) return { variant: 'simple', message: 'Call error' };
  if (typeof error === 'string') return { variant: 'simple', message: error };

  const code = error?.code;
  const message = error?.message || error?.msg || 'Call error';

  if (code === CallErrorCode.NoUserMedia || error?.mediaKind) {
    return getCallMediaErrorPresentation(error);
  }

  switch (code) {
    case CallErrorCode.IceFailed:
      return {
        variant: 'simple',
        message:
          'Could not establish a media connection (ICE failed). Check your network and try again.',
      };
    case CallErrorCode.InviteTimeout:
      return { variant: 'simple', message: 'Call timed out. Please try again.' };
    default:
      return { variant: 'simple', message };
  }
};

/**
 * Meet/Zoom-style media access / call error card.
 * `onRetryAccess` must be wired to a user-gesture handler (re-acquire getUserMedia).
 */
const CallErrorState = ({ callError, onDismiss, onOpenSettings, onRetryAccess }) => {
  const presentation = useMemo(() => getCallErrorPresentation(callError), [callError]);
  const showPrimary =
    (presentation.variant === 'permission' || presentation.variant === 'retry') &&
    typeof onRetryAccess === 'function';

  return (
    <div className="call-wrapper call-wrapper_backdrop_blur d-flex flex-column align-items-center justify-content-center py-4">
      <div className="call-card p-4 text-center call-error-card position-relative">
        <button
          type="button"
          className="call-error-close"
          onClick={onDismiss}
          aria-label="Close"
        >
          ×
        </button>
        {presentation.variant === 'permission' || presentation.variant === 'retry' ? (
          <>
            <h2 className="call-error-title mb-2">{presentation.title}</h2>
            <p className="call-error-body mb-3">{presentation.body}</p>
            <div className="d-flex flex-column align-items-center gap-2">
              {showPrimary && (
                <button
                  type="button"
                  className="jh_btn_pink call-error-open-settings"
                  onClick={onRetryAccess}
                >
                  {presentation.primaryLabel || 'Allow access'}
                </button>
              )}
              {typeof onOpenSettings === 'function' && presentation.variant === 'permission' && (
                <button
                  type="button"
                  className="btn btn-link call-error-body p-0"
                  onClick={onOpenSettings}
                >
                  Open settings
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="call-error-body mb-3">{presentation.message}</p>
        )}
      </div>
    </div>
  );
};

export default CallErrorState;
